import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Play, CheckCircle2, XCircle, Clock, Activity } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExecutionMonitor = () => {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchExecutions();
    const interval = setInterval(fetchExecutions, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchExecutions = async () => {
    try {
      const response = await axios.get(`${API}/executions`);
      setExecutions(response.data);
    } catch (error) {
      console.error('Error fetching executions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-green-400" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-400" />;
      case 'running':
        return <Activity className="w-5 h-5 text-blue-400 animate-pulse" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="executions-page">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Execution Monitor
        </h1>
        <p className="text-sm sm:text-base text-gray-400">Real-time monitoring of agent and workflow executions</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="metric-card" data-testid="stat-total-executions">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
              <Activity className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total</p>
              <h3 className="text-2xl font-bold text-white">{executions.length}</h3>
            </div>
          </div>
        </div>
        <div className="metric-card" data-testid="stat-completed">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Completed</p>
              <h3 className="text-2xl font-bold text-white">
                {executions.filter((e) => e.status === 'completed').length}
              </h3>
            </div>
          </div>
        </div>
        <div className="metric-card" data-testid="stat-running">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Running</p>
              <h3 className="text-2xl font-bold text-white">
                {executions.filter((e) => e.status === 'running').length}
              </h3>
            </div>
          </div>
        </div>
        <div className="metric-card" data-testid="stat-failed">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Failed</p>
              <h3 className="text-2xl font-bold text-white">
                {executions.filter((e) => e.status === 'failed').length}
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* Executions Table */}
      <div className="glass-panel overflow-hidden" data-testid="executions-table">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-4 text-gray-400 font-medium">ID</th>
                <th className="text-left p-4 text-gray-400 font-medium">Workflow/Agent</th>
                <th className="text-left p-4 text-gray-400 font-medium">Status</th>
                <th className="text-left p-4 text-gray-400 font-medium">Started</th>
                <th className="text-left p-4 text-gray-400 font-medium">Duration</th>
                <th className="text-left p-4 text-gray-400 font-medium">Logs</th>
              </tr>
            </thead>
            <tbody>
              {executions.map((execution) => (
                <tr
                  key={execution.id}
                  className="border-b border-white/5 hover:bg-white/5 transition-colors"
                  data-testid={`execution-row-${execution.id}`}
                >
                  <td className="p-4 text-gray-300 text-sm font-mono">{execution.id.slice(0, 8)}</td>
                  <td className="p-4">
                    <div>
                      <p className="text-white font-medium">{execution.workflow_id}</p>
                      {execution.agent_id && (
                        <p className="text-gray-400 text-xs">Agent: {execution.agent_id.slice(0, 8)}</p>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(execution.status)}
                      <span className={`status-badge status-${execution.status}`}>{execution.status}</span>
                    </div>
                  </td>
                  <td className="p-4 text-gray-300 text-sm">
                    {new Date(execution.started_at).toLocaleString()}
                  </td>
                  <td className="p-4 text-gray-300 text-sm">
                    {execution.completed_at
                      ? `${Math.round(
                          (new Date(execution.completed_at) - new Date(execution.started_at)) / 1000
                        )}s`
                      : '-'}
                  </td>
                  <td className="p-4 text-gray-300 text-sm">
                    {execution.logs.length > 0 ? (
                      <span className="text-blue-400 cursor-pointer hover:underline">
                        View {execution.logs.length} logs
                      </span>
                    ) : (
                      <span className="text-gray-500">No logs</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {executions.length === 0 && (
            <div className="text-center py-12">
              <Activity className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No executions yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExecutionMonitor;